import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Avatar,
  Rating
} from '@mui/material';
import {
  Newspaper as NewsIcon,
  Videocam as VideoIcon,
  TrendingUp as AnalyticsIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Palette as DesignIcon,
  Language as MultiLangIcon,
  Cloud as CloudIcon,
  PlayArrow as PlayIcon,
  CheckCircle as CheckIcon,
  Star as StarIcon
} from '@mui/icons-material';

import featuresImage from '../assets/arka2.jpeg';

const FeaturesRevamp = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [isInView, setIsInView] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const sectionRef = useRef(null);

  const categories = [
    { label: 'İçerik Yönetimi', value: 'content' },
    { label: 'Tasarım & UX', value: 'design' },
    { label: 'Performans', value: 'performance' },
    { label: 'Güvenlik', value: 'security' }
  ];

  const features = {
    content: [
      {
        icon: <NewsIcon sx={{ fontSize: 32 }} />,
        title: 'Lightning Haber Editörü',
        description: 'Breaking news\'i 30 saniyede hazırlayın. Otomatik başlık önerisi ve SEO.',
        benefits: ['Hızlı haber girişi', 'Otomatik etiketleme', 'Fact-check uyarıları', 'Çoklu dil desteği'],
        color: '#3b82f6',
        stats: { value: '30sn', label: 'Haber Hazırlama' }
      },
      {
        icon: <VideoIcon sx={{ fontSize: 32 }} />,
        title: 'Canlı Yayın Stüdyosu',
        description: 'Önemli gelişmeleri canlı yayınlayın, interaktif habercilik yapın',
        benefits: ['Multi-camera desteği', 'Chat entegrasyonu', 'Sosyal medya sync', 'Arşiv kayıt'],
        color: '#ef4444',
        stats: { value: '24/7', label: 'Canlı Yayın' }
      },
      {
        icon: <AnalyticsIcon sx={{ fontSize: 32 }} />,
        title: 'Okuyucu Pulse Sistemi',
        description: 'Hangi haberler viral oluyor, okuyucular ne istiyor?',
        benefits: ['Trending algoritması', 'Okuyucu yorumları', 'Sosyal medya takibi', 'Rakip analizi'],
        color: '#10b981',
        stats: { value: '47K', label: 'Dakika Okur' }
      }
    ],
    design: [
      {
        icon: <DesignIcon sx={{ fontSize: 32 }} />,
        title: 'Haber Sitesi Tema Galerisi',
        description: 'Gazeteciliğe özel tasarımlar. Spor, ekonomi, magazin kategorilerine uygun temalar.',
        benefits: ['Kategori temalar', 'Breaking news layoutu', 'Mobil-first tasarım', 'AMP desteği'],
        color: '#8b5cf6',
        stats: { value: '25+', label: 'Haber Teması' }
      },
      {
        icon: <MultiLangIcon sx={{ fontSize: 32 }} />,
        title: 'Küresel Habercilik',
        description: 'Türkiye\'den dünyaya, dünyadan Türkiye\'ye haber akışı',
        benefits: ['Otomatik çeviri', 'Yerel habercilik', 'Uluslararası wire', 'Zaman dilimi sync'],
        color: '#f59e0b',
        stats: { value: '15', label: 'Ülke Desteği' }
      }
    ],
    performance: [
      {
        icon: <SpeedIcon sx={{ fontSize: 32 }} />,
        title: 'Breaking News Hızı',
        description: 'Son dakika haberleri saniyeler içinde tüm dünyaya yayılsın',
        benefits: ['Push notification', 'Instant indexing', 'Social auto-post', 'AMP sayfalar'],
        color: '#06b6d4',
        stats: { value: '12sn', label: 'Yayın Hızı' }
      },
      {
        icon: <CloudIcon sx={{ fontSize: 32 }} />,
        title: 'Trafik Patlaması Yönetimi',
        description: 'Viral haberleriniz milyonlara ulaştığında sistem çökmez',
        benefits: ['Otomatik scaling', 'DDoS koruması', 'Cache optimization', '7/24 monitoring'],
        color: '#84cc16',
        stats: { value: '10M+', label: 'Eşzamanlı Okur' }
      }
    ],
    security: [
      {
        icon: <SecurityIcon sx={{ fontSize: 32 }} />,
        title: 'Gazetecilik Etiği & Güvenlik',
        description: 'Kaynak koruması, haber doğruluğu ve editöryal bağımsızlık',
        benefits: ['Kaynak anonimliği', 'Fact-check sistemi', 'Editör hierarşisi', 'Hukuki koruma'],
        color: '#dc2626',
        stats: { value: '100%', label: 'Kaynak Koruması' }
      }
    ]
  };


  // Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Auto-rotate features
  // useEffect(() => {
  //   if (!isInView) return;

  //   const currentFeatures = features[categories[activeTab].value];
  //   const interval = setInterval(() => {
  //     setActiveFeature((prev) => (prev + 1) % currentFeatures.length);
  //   }, 4000);

  //   return () => clearInterval(interval);
  // }, [isInView, activeTab, features, categories]);

  const currentFeatures = features[categories[activeTab].value];
  const currentFeature = currentFeatures[activeFeature];

  return (
    <Box
      ref={sectionRef}
      sx={{
        py: { xs: 8, md: 12 },
        background: `
          linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)`,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 20%, ${currentFeature?.color}15 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, ${currentFeature?.color}10 0%, transparent 50%)
          `,
          transition: 'all 1s ease',
          pointerEvents: 'none'
        }
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        {/* Header */}
        <Box textAlign="center" mb={8}>
          <Chip
            label="ÖZELLİKLER"
            sx={{
              background: `linear-gradient(135deg, ${currentFeature?.color}20, ${currentFeature?.color}40)`,
              color: currentFeature?.color,
              border: `1px solid ${currentFeature?.color}30`,
              backdropFilter: 'blur(10px)',
              fontSize: '0.9rem',
              fontWeight: 600,
              mb: 4,
              animation: isInView ? 'fadeInUp 0.6s ease-out' : 'none'
            }}
          />
          
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2.8rem', md: '4rem' },
              fontWeight: 900,
              mb: 3,
              color: 'white',
              background: `linear-gradient(135deg, #ffffff 0%, ${currentFeature?.color} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation: isInView ? 'fadeInUp 0.8s ease-out 0.2s both' : 'none'
            }}
          >
            Profesyonel
            <br />
            <span style={{ color: currentFeature?.color }}>Haber Platformu</span>
          </Typography>
          
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              maxWidth: '600px',
              mx: 'auto',
              lineHeight: 1.6,
              animation: isInView ? 'fadeInUp 1s ease-out 0.4s both' : 'none'
            }}
          >
            Modern gazeteciliğin ihtiyaçlarına özel tasarlanmış 
            güçlü araçlar ve özellikler
          </Typography>
        </Box>

        {/* Category Tabs */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 6,
            animation: isInView ? 'fadeInUp 1.2s ease-out 0.6s both' : 'none'
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => {
              setActiveTab(newValue);
              setActiveFeature(0);
            }}
            sx={{
              '& .MuiTabs-indicator': {
                background: currentFeature?.color,
                height: '3px',
                borderRadius: '2px'
              },
              '& .MuiTab-root': {
                color: 'rgba(255, 255, 255, 0.6)',
                fontWeight: 600,
                fontSize: '1rem',
                textTransform: 'none',
                px: 3,
                py: 2,
                '&.Mui-selected': {
                  color: currentFeature?.color
                }
              }
            }}
          >
            {categories.map((category, index) => (
              <Tab key={index} label={category.label} />
            ))}
          </Tabs>
        </Box>

        {/* Main Feature Showcase */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
            gap: 6,
            alignItems: 'center',
            mb: 8
          }}
        >
          {/* Left - Feature Details */}
          <Box>
            <Card
              sx={{
                background: `linear-gradient(135deg, ${currentFeature?.color}10, ${currentFeature?.color}05)`,
                backdropFilter: 'blur(20px)',
                border: `1px solid ${currentFeature?.color}30`,
                borderRadius: '24px',
                p: 4,
                transition: 'all 0.8s ease'
              }}
            >
              <CardContent sx={{ p: 0 }}>
                {/* Icon & Stats */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 3
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '20px',
                      background: `linear-gradient(135deg, ${currentFeature?.color}, ${currentFeature?.color}dd)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      boxShadow: `0 10px 30px ${currentFeature?.color}30`
                    }}
                  >
                    {currentFeature?.icon}
                  </Box>
                  
                  <Box textAlign="right">
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 900,
                        color: currentFeature?.color,
                        mb: 0.5
                      }}
                    >
                      {currentFeature?.stats.value}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontWeight: 600
                      }}
                    >
                      {currentFeature?.stats.label}
                    </Typography>
                  </Box>
                </Box>

                {/* Title & Description */}
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    color: 'white',
                    mb: 2,
                    fontSize: { xs: '1.8rem', md: '2.2rem' }
                  }}
                >
                  {currentFeature?.title}
                </Typography>
                
                <Typography
                  variant="body1"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    lineHeight: 1.6,
                    mb: 4,
                    fontSize: '1.1rem'
                  }}
                >
                  {currentFeature?.description}
                </Typography>

                {/* Benefits */}
                <Stack spacing={2} mb={4}>
                  {currentFeature?.benefits.map((benefit, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        animation: `slideInLeft 0.6s ease-out ${index * 0.1}s both`
                      }}
                    >
                      <CheckIcon
                        sx={{
                          color: currentFeature?.color,
                          fontSize: 20
                        }}
                      />
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'rgba(255, 255, 255, 0.9)',
                          fontWeight: 500
                        }}
                      >
                        {benefit}
                      </Typography>
                    </Box>
                  ))}
                </Stack>

                {/* CTA */}
                <Button
                  variant="contained"
                  startIcon={<PlayIcon />}
                  sx={{
                    background: `linear-gradient(135deg, ${currentFeature?.color}, ${currentFeature?.color}dd)`,
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    borderRadius: '12px',
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: `0 8px 25px ${currentFeature?.color}40`,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 12px 35px ${currentFeature?.color}50`
                    }
                  }}
                >
                  Canlı Demo
                </Button>
              </CardContent>
            </Card>
          </Box>

          {/* Right - Feature Navigation */}
          <Box>
            <Stack spacing={3}>
              {currentFeatures.map((feature, index) => (
                <Card
                  key={index}
                  onClick={() => setActiveFeature(index)}
                  sx={{
                    background: activeFeature === index
                      ? `linear-gradient(135deg, ${feature.color}15, ${feature.color}08)`
                      : 'rgba(255, 255, 255, 0.03)',
                    backdropFilter: 'blur(20px)',
                    border: activeFeature === index
                      ? `2px solid ${feature.color}50`
                      : '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: '16px',
                    p: 3,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    transform: activeFeature === index ? 'translateX(8px)' : 'translateX(0)',
                    '&:hover': {
                      transform: 'translateX(12px)',
                      border: `2px solid ${feature.color}40`
                    }
                  }}
                >
                  <CardContent sx={{ p: 0 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 3
                      }}
                    >
                      <Box
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: '12px',
                          background: `${feature.color}20`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: feature.color,
                          fontSize: '24px'
                        }}
                      >
                        {feature.icon}
                      </Box>
                      
                      <Box flex={1}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: 'white',
                            mb: 0.5
                          }}
                        >
                          {feature.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'rgba(255, 255, 255, 0.6)',
                            fontSize: '0.9rem'
                          }}
                        >
                          {feature.description.slice(0, 60)}...
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: activeFeature === index ? feature.color : 'transparent',
                          transition: 'all 0.3s ease'
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Box>
        </Box>
      </Container>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </Box>
  );
};

export default FeaturesRevamp;







